import _, { sortBy } from "lodash";
import ReflectionAttribute from "./ReflectionAttribute";
import ReflectionClass from "./ReflectionClass";
import ReflectionClassConfig from "./ReflectionClassConfig";
import ReflectionDomainClass from "./ReflectionDomainClass";
import ReflectionEnum from "./ReflectionEnum";
import { AttributeGroup, AttributeGroupData } from "./reflectionTypes";
import { PATH_PARENT } from "./reflectionConstants";
import { C8AttributeId, C8Node, C8NodeType } from "src/c8-platform/type/node";
import { C8AttributeType, VALUE_CONTAINER_TYPE, VALUE_TYPE } from "src/c8-platform/type/value";

const {
	CNL,
	BOOLEAN,
	DOUBLE,
	ENUM,
	EXPRESSION,
	FLOAT,
	INT64,
	INTEGER,
	REF,
	STRING,
	TIME,
	URL,
} = VALUE_TYPE;

const {
	LIST,
	MAP,
	SET,
	VECTOR,
} = VALUE_CONTAINER_TYPE;

// Object: class name to class instance map
const _classHash: Record<C8NodeType, ReflectionClass> = {};
const _classConfigHash: Record<C8NodeType, ReflectionClassConfig> = {};
const _domainHash: Record<C8NodeType, ReflectionDomainClass> = {};
const _enumHash: Record<string, ReflectionEnum> = {};
const _aggregateStoreSet = new Set<string>();

const _containerTypes = [LIST, VECTOR, SET, MAP];
const _containerScalar: Record<string, number> = {
	[LIST]: 20,
	[VECTOR]: 40,
	[SET]: 60,
	[MAP]: 80,
};

const _indexToTypeMap: Record<number, C8AttributeType> = {};

function setIndexToType(scalarIndex: number, scalarType: C8AttributeType): void {
	_indexToTypeMap[scalarIndex] = scalarType;

	_containerTypes.forEach((containerType) => {
		_indexToTypeMap[scalarIndex + _containerScalar[containerType]] = (scalarType + containerType) as C8AttributeType;
	});
}

setIndexToType(0, STRING);
setIndexToType(1, INTEGER);
setIndexToType(2, BOOLEAN);
setIndexToType(3, DOUBLE);
setIndexToType(4, ENUM);
setIndexToType(5, REF);
setIndexToType(6, TIME);
setIndexToType(7, URL);
setIndexToType(8, INT64);
setIndexToType(9, EXPRESSION);
setIndexToType(10, FLOAT);
setIndexToType(11, CNL);


export function setClassHash(type: C8NodeType, c: ReflectionClass): void {
	_classHash[type] = c;
}

export function setClassConfigHash(type: C8NodeType, c: ReflectionClassConfig): void {
	_classConfigHash[type] = c;
}

export function setDomainHash(type: C8NodeType, c: ReflectionDomainClass): void {
	_domainHash[type] = c;
}

export function setEnumList(id: string, enumList: ReflectionEnum): void {
	_enumHash[id] = enumList;
}

export function setAttributesWithAggregateStores(className: C8NodeType, attributeIds: C8AttributeId[]): void {
	for (const attrId of attributeIds) {
		_aggregateStoreSet.add(className + "." + attrId);
	}
}

export function doesAttributeHaveAggregateStore(className: C8NodeType, attributeId: C8AttributeId): boolean {
	return _aggregateStoreSet.has(className + "." + attributeId);
}

export function getTypeByIndex(index: string): C8AttributeType {
	const type = _indexToTypeMap[index];
	if (!type) {
		console.warn(`Cannot find type for index "${index}"`);
		return STRING;
	}

	return type;
}

export function getAttributeByClassNameAndId(className: C8NodeType, attrId: C8AttributeId): ReflectionAttribute {
	const rc = getClass(className);
	if (!rc) {
		console.warn(`Cannot find the class"${className}"`);
		return null;
	}

	return rc.getAttribute(attrId, false, false, true);
}

/**
 * Get scalar value that represents compressed attribute type
 */
export function getAttributeTypeScalar(type: C8AttributeType): number {
	const invertedMap = _.invert(_indexToTypeMap);
	return Number(invertedMap[type]);
}

/**
 * Return reflection class for given type
 */
export function getClass(value: C8NodeType | ReflectionClass): ReflectionClass {
	return (typeof value === "string") ? _classHash[value] : value;
}

/**
 * Return reflection class config for given type
 */
export function getClassConfig(type: C8NodeType): ReflectionClassConfig {
	return _classConfigHash[type];
}

/**
 * Return array of all reflection classes
 */
export function getClasses(): ReflectionClass[] {
	return _.values(_classHash);
}

/**
* Return Domain Class given type.
*/
export function getDomainClass(type: C8NodeType): ReflectionDomainClass {
	return _domainHash[type];
}

export function getClassAttribute(rc: ReflectionClass, id: string): ReflectionAttribute {
	if (!rc || !id || !rc.getAttribute) {
		return null;
	}
	return rc.getAttribute(id, true, true, true);
}

export function getAttribute(node: C8Node, id: C8AttributeId): ReflectionAttribute | null {
	return node ? getClassAttribute(getClass(node.$Type), id) : null;
}

/**
 * Return list of all Domain Classes.
 */
export function getDomainClasses(): ReflectionDomainClass[] {
	return _.values(_domainHash);
}


export function getEnumList(id: string): ReflectionEnum {
	return _enumHash[id];
}

/**
 * Return Reflection Classes given list of types.
 */
export function getClassesByTypes(types: C8NodeType[]): ReflectionClass[] {
	return types.map(getClass);
}

/** create attribute groups from reflection data */
export function createAttributeGroups(groupDefs?: AttributeGroupData[]): AttributeGroup[] {
	const groups: AttributeGroup[] = [];
	const names = new Set<string>();

	groupDefs?.forEach((groupDef) => {
		const [name, attributes, fromMaster] = groupDef;

		// skip empty or duplicate groups
		if (!names.has(name) && attributes?.length) {
			const keys = attributes.map((id, index) => ({
				id,
				path: fromMaster?.[index] ? PATH_PARENT : null,
			}));

			groups.push({ keys, name });
			names.add(name);
		}
	});

	return sortBy(groups, ({ name }) => name);
}
